import { useCallback, useEffect, useRef } from 'react';

/**
 * Anti-scraping contact display — hardened edition.
 *
 * Defence layers:
 * 1. Text assembled from char codes at render — never a plain string in bundle
 * 2. Zero-width + invisible Unicode decoys injected between every real char
 * 3. No mailto: or tel: links in static HTML — generated on user click via JS
 * 4. CSS: user-select none, pointer-events controlled, no drag
 * 5. JS: copy/cut/drag events intercepted and neutralised
 * 6. MutationObserver re-poisons text if DOM is tampered
 */

// 0216 606 8027
const _p = [48,50,49,54,32,54,48,54,32,56,48,50,55];
// info@veperainsaat.com
const _e = [105,110,102,111,64,118,101,112,101,114,97,105,110,115,97,97,116,46,99,111,109];
// Bilgin Sok. Kartal/İstanbul
const _a = [66,105,108,103,105,110,32,83,111,107,46,32,75,97,114,116,97,108,47,304,115,116,97,110,98,117,108];

const buildStr = (codes) => codes.map(c => String.fromCharCode(c)).join('');

const ZWSP  = '\u200B';
const ZWNJ  = '\u200C';
const ZWJ   = '\u200D';
const WJ    = '\u2060';
const SHY   = '\u00AD';
const decoys = [ZWSP, ZWNJ, ZWJ, WJ, SHY];

const ProtectedText = ({ codes, className, style }) => {
  const ref = useRef(null);
  const text = buildStr(codes);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const block = (e) => { e.preventDefault(); e.stopPropagation(); };
    el.addEventListener('copy', block);
    el.addEventListener('cut', block);
    el.addEventListener('dragstart', block);
    el.addEventListener('selectstart', block);
    return () => {
      el.removeEventListener('copy', block);
      el.removeEventListener('cut', block);
      el.removeEventListener('dragstart', block);
      el.removeEventListener('selectstart', block);
    };
  }, []);

  return (
    <span
      ref={ref}
      className={className}
      style={{
        ...style,
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        WebkitTouchCallout: 'none',
      }}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      draggable={false}
    >
      {text.split('').map((char, i) => (
        <span key={i} data-v="">
          {char}
          {i < text.length - 1 && (
            <span aria-hidden="true" style={{ fontSize: 0, lineHeight: 0, position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}>
              {decoys[i % decoys.length]}{decoys[(i + 2) % decoys.length]}
            </span>
          )}
        </span>
      ))}
    </span>
  );
};

export const SafePhone = ({ className, style }) => (
  <ProtectedText codes={_p} className={className} style={style} />
);

export const SafeEmail = ({ className, style }) => (
  <ProtectedText codes={_e} className={className} style={style} />
);

export const SafeAddress = ({ className, style }) => (
  <ProtectedText codes={_a} className={className} style={style} />
);

export const usePhoneClick = () =>
  useCallback(() => {
    window.location.href = 'te' + 'l:+9' + buildStr([50,49,54,54,48,54,56,48,50,55]);
  }, []);

export const useEmailClick = () =>
  useCallback(() => {
    window.location.href = 'mai' + 'lto:' + buildStr(_e);
  }, []);
